import type { useSingleton } from "@tippyjs/react";
import type { KeyHandler } from "hotkeys-js";
import { useAtomValue } from "jotai";
import { type KeyboardEventHandler, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { ChevronDownIcon, ChevronUpIcon, CloseIcon, SearchIcon } from "@/icons";
import { searchResultAtom } from "@/interface/AppStore";
import { useHotkeys } from "@/interface/hooks";
import { Tooltip } from "@/interface/ui/Tooltip";
import { searchController } from "@/searchController";
import { BottomBarItem } from "../BottomBarItem";
import styles from "./BottomBarSearch.module.css";

export const BottomBarSearch = ({
    singletonTarget,
}: {
    singletonTarget: ReturnType<typeof useSingleton>[1];
}) => {
    const { t } = useTranslation();
    const [showInput, setShowInput] = useState(false);
    const [value, setValue] = useState("");
    const results = useAtomValue(searchResultAtom);
    const handleSearchHotkey = useCallback<KeyHandler>((event, _handler) => {
        event.preventDefault();
        setShowInput(true);
    }, []);
    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === "Enter") {
            searchController.nextItem();
        } else if (e.key === "Escape") {
            setShowInput(false);
        } else if ((e.ctrlKey && e.code === "KeyF") || (e.metaKey && e.code === "KeyF")) {
            // иначе выводится и окно поиска в браузере
            e.preventDefault();
        }
    };
    useHotkeys("ctrl+f, cmd+f", handleSearchHotkey);
    useEffect(() => {
        searchController.inputSearch(value.trim().toLowerCase());
    }, [value]);
    useEffect(() => {
        if (!showInput) {
            searchController.reset();
            setValue("");
        }
    }, [showInput]);
    const renderSearchCounters = () => {
        if (!value.trim()) {
            return "";
        }
        if (!results?.total) {
            return t("bottomBar.searchEmpty");
        }
        return t("bottomBar.searchCounter", {
            current: (results.currentIndex || 0) + 1,
            total: results.total,
        });
    };
    return (
        <div className={styles.wrapper}>
            <Tooltip
                content={t("bottomBar.search")}
                hotkey={`${canv.actionManager.actionSymbol} + F`}
                singleton={singletonTarget}
            >
                <BottomBarItem active={showInput} onClick={() => setShowInput((o) => !o)}>
                    <SearchIcon />
                </BottomBarItem>
            </Tooltip>
            {showInput ? (
                <div className={styles.inputWrapper}>
                    <input
                        className={styles.input}
                        placeholder={t("bottomBar.searchPlaceholder")}
                        name="bottomBarSearchInput"
                        value={value}
                        onChange={(e) => {
                            setValue(e.currentTarget.value);
                        }}
                        onBlur={() => {
                            canv.appInterface.inputFocused = 0;
                        }}
                        onFocus={() => {
                            canv.appInterface.inputFocused = 1;
                        }}
                        onKeyDown={handleKeyDown}
                        // biome-ignore lint/a11y/noAutofocus: focus on open
                        autoFocus
                    />
                    <div className={styles.results}>{renderSearchCounters()}</div>
                    <div className={styles.actions}>
                        <button
                            className={styles.action}
                            disabled={(results.total ?? 0) <= 1}
                            type="button"
                            onClick={() => {
                                searchController.previousItem();
                            }}
                        >
                            <ChevronUpIcon />
                        </button>
                        <button
                            className={styles.action}
                            disabled={(results.total ?? 0) <= 1}
                            type="button"
                            onClick={() => {
                                searchController.nextItem();
                            }}
                        >
                            <ChevronDownIcon />
                        </button>
                        <button
                            className={styles.action}
                            type="button"
                            onClick={() => {
                                setShowInput(false);
                            }}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
